import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { Input } from '../ui/input'

interface Brand {
  id: string
  name: string
  slug: string
}

interface BrandAutocompleteProps {
  value: string
  onChange: (brandName: string, brandId?: string) => void
  placeholder?: string
  required?: boolean
}

export function BrandAutocomplete({ value, onChange, placeholder = 'e.g., Patagonia', required = false }: BrandAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value)
  const [suggestions, setSuggestions] = useState<Brand[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchBrands = async (query: string) => {
    if (!query || query.trim().length === 0) {
      setSuggestions([])
      return
    }

    try {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name, slug')
        .or(`name.ilike.%${query}%,slug.ilike.%${query}%`)
        .order('name')
        .limit(10)

      if (error) {
        console.error('Error fetching brands:', error)
        return
      }

      setSuggestions(data || [])
    } catch (error) {
      console.error('Error fetching brands:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    setSelectedIndex(-1)

    // Fetch suggestions
    if (newValue.trim().length > 0) {
      fetchBrands(newValue)
      setShowSuggestions(true)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }

    // Update parent with text only (no brand_id since it's not selected from list yet)
    onChange(newValue, undefined)
  }

  const handleSelectBrand = (brand: Brand) => {
    setInputValue(brand.name)
    setSuggestions([])
    setShowSuggestions(false)
    onChange(brand.name, brand.id)
  }

  const handleCreateNew = async () => {
    if (!inputValue.trim()) return

    try {
      // Call normalize-brand-name to create the brand with AI normalization
      const { data, error } = await supabase.functions.invoke('normalize-brand-name', {
        body: { brand: inputValue }
      })

      if (error) {
        console.error('Error creating brand:', error)
        // Still allow the user to proceed with the typed value
        setShowSuggestions(false)
        return
      }

      if (data?.brand_id && data?.brand_name) {
        setInputValue(data.brand_name)
        onChange(data.brand_name, data.brand_id)
      }

      setShowSuggestions(false)
    } catch (error) {
      console.error('Error creating brand:', error)
      setShowSuggestions(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < suggestions.length ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > -1 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex === -1) {
          // No selection, treat as "Create new"
          handleCreateNew()
        } else if (selectedIndex === suggestions.length) {
          // "Create new" option selected
          handleCreateNew()
        } else if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          // Brand selected
          handleSelectBrand(suggestions[selectedIndex])
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setSelectedIndex(-1)
        break
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        id="brand"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true)
        }}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />

      {showSuggestions && (suggestions.length > 0 || inputValue.trim().length > 0) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((brand, index) => (
            <button
              key={brand.id}
              type="button"
              onClick={() => handleSelectBrand(brand)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors ${
                index === selectedIndex ? 'bg-gray-100' : ''
              }`}
            >
              <div className="font-medium text-gray-900">{brand.name}</div>
            </button>
          ))}

          {inputValue.trim().length > 0 && (
            <button
              type="button"
              onClick={handleCreateNew}
              className={`w-full text-left px-4 py-2 border-t border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors ${
                selectedIndex === suggestions.length ? 'bg-gray-100' : ''
              }`}
            >
              <div className="font-medium text-gray-900">
                Create "{inputValue}"
              </div>
              <div className="text-xs text-gray-500">
                Create a new brand
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
